import DOMPurify from "dompurify";

export function transitionDuration(target: Element, mode = "ms") {
  const styles = window.getComputedStyle(target);
  const delay = durationInMS(styles.getPropertyValue("transition-duration")) + durationInMS(styles.getPropertyValue("transition-delay"));

  return mode == "ms" ? delay : delay / 1000;

  function durationInMS(duration: string) {
    let ms = 0;
    duration = duration.split(",")[0];
    if (duration.endsWith("ms") || !duration.endsWith("s")) {
      duration = duration.replace("ms", "");
      ms = +duration;
    }

    if (duration.endsWith("s")) {
      duration = duration.replace("s", "");
      ms = +duration * 1000;
    }

    return ms;
  }
}

export function getHighestZIndex(selector?: string) {
  if (!selector) selector = "*";
  const elements = Array.from(document.querySelectorAll(selector));

  let highest = 0;
  for (let i = 0; i < elements.length; i++) {
    const zIndex = document.defaultView?.getComputedStyle(elements[i], null).getPropertyValue("z-index") ?? 0;
    if (zIndex != "auto" && +zIndex > highest) {
      highest = +zIndex;
    }
  }
  return highest;
}

/**
 * Search the starting element and its parentNode tree. The first qualifying element is returned, if none are found 'null'.
 * ```
 * // Create Sample DOM elements
 * var el = document.CreateElement('div');
 * el.setAttribute('target-attribute', 'some value');
 * el.insertAdjacentHTML('beforeend', '<button class="btn btn-danger"></button>');
 * var btn = el.querySelector('button');
 * // Run Samples
 * SeekElementInBranch(btn, 'hasClass', 'btn-danger'); //btn
 * SeekElementInBranch(btn, 'hasClass', 'btn-primary'); //null
 * SeekElementInBranch(btn, 'hasAttribute', 'target-attribute'); //el
 * SeekElementInBranch(btn, 'hasAttribute', 'missing-attribute'); //null
 * ```
 */
export function SeekElementInBranch(
  startingElement: HTMLElement,
  searchMethod: "hasId" | "hasClass" | "hasAttribute" | "hasNodeName" | "hasDataSet" | "hasDataSetValue",
  searchTerm: string,
  dataSetValue?: string
) {
  let target: HTMLElement | null = startingElement;

  switch (searchMethod) {
    case "hasId": {
      while (target) {
        if (target.id == searchTerm) break;
        target = target.parentElement;
      }
      break;
    }
    case "hasClass": {
      while (target) {
        if (target.classList && target.classList.contains(searchTerm)) break;
        target = target.parentElement;
      }
      break;
    }
    case "hasAttribute": {
      while (target) {
        if (target.hasAttribute && target.hasAttribute(searchTerm)) break;
        target = target.parentElement;
      }
      break;
    }
    case "hasNodeName": {
      while (target) {
        if (target.nodeName && target.nodeName == searchTerm.toUpperCase()) break;
        target = target.parentElement;
      }
      break;
    }
    case "hasDataSet": {
      while (target) {
        if (target.dataset && target.dataset[searchTerm]) break;
        target = target.parentElement;
      }
      break;
    }
    case "hasDataSetValue": {
      while (target) {
        if (target.dataset && target.dataset[searchTerm]) {
          if (target.dataset[searchTerm] == dataSetValue) break;
          if (!dataSetValue) break;
        }
        target = target.parentElement;
      }
      break;
    }
    default:
      throw "Invalid searchMethod";
  }

  return target ? target : null;
}

export interface RGB {
  r: number;
  b: number;
  g: number;
  readonly brightnessThreshold: number;
  readonly brightness: number;
  readonly isDark: boolean;
  readonly isLight: boolean;
}

export const Colors = {
  rgb: RGB,

  /**
   * Get an RGB colors brightness using the calculation ([Source](https://www.w3.org/TR/AERT/#color-contrast)):
   * ```
   * ((Red value X 299) + (Green value X 587) + (Blue value X 114)) / 1000
   * ```
   */
  brightness: Brightness,

  /**
   * Create a RGB Object from a 3/6 digit hex string
   */
  hexToRgb: HexToRgb,

  rgbFromString: RGBFromString,
};

function Brightness(rgb: RGB) {
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
}

/**
 * Create a RGB Object from a 3/6 digit hex string
 */
function HexToRgb(hex: string) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? RGB(parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)) : null;
}

/**
 * Create a RGB Object from a 3/6 digit hex or rbg/rgba string
 */
function RGBFromString(color: string) {
  color = color.toLowerCase();
  if (color.startsWith("#")) return HexToRgb(color);
  if (color.startsWith("rgb")) {
    color = color.replace("rgb", "");
    color = color.replace("a", "");
    color = color.replace("(", "");
    color = color.replace(")", "");
    color = color.replace(" ", "");
    const colorParts = color.split(",").map(x => Number(x));
    return RGB(colorParts[0], colorParts[1], colorParts[2]);
  }

  return null;
}

function RGB(r: number, b: number, g: number): RGB {
  const rgb = {
    r,
    b,
    g,
    /**
     * Minimum brightness value for light colors
     */
    get brightnessThreshold() {
      return 170;
    },

    /**
     * Get the rgb value's brightness
     */
    get brightness(): number {
      return Brightness(this);
    },

    /**
     * Check if the rgb value is a dark color
     */
    get isDark() {
      return this.brightness <= this.brightnessThreshold;
    },

    /**
     * Check if the rgb value is a light color
     */
    get isLight() {
      return this.brightness > this.brightnessThreshold;
    },
  };

  return rgb;
}

export function unitSizeToPixels(size: string) {
  let unitSize = "";
  let unitType = "";

  size = size.toLowerCase();
  if (size.indexOf("%") > -1) {
    unitSize = size.split("%")[0];
    unitType = "%";
  } else {
    unitSize = size.split(/[A-Za-z]/)[0];
    unitType = size.replace(unitSize, "");
  }

  if (isNaN(+unitSize)) return "0px";

  switch (unitType) {
    case "px":
      return size;
    case "pt":
      return Math.round(+unitSize * 1.33) + "px";
    case "em":
    case "rem":
      return +unitSize * 16 + "px";
    case "%":
      return 16 * (+unitSize / 100) + "px";
  }

  return "0px";
}

export function sanitizeHTML(val: string) {
  return DOMPurify.sanitize(val, {
    /* ---------- Explicit tag allow‑list ---------- */
    /* ---------- Only what we want to support in banners ---------- */
    ALLOWED_TAGS: [
      "div",
      "p",
      "b",
      "strong",
      "em",
      "span",
      "a",
      "br",
      "u",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "li",
      "ul",
      "ol",
      "i", // Font Awesome icons
    ],

    /* ---------- Explicit attribute allow‑list ---------- */
    ALLOWED_ATTR: [
      "href",
      "target",
      "rel",
      "title",
      "style",
      "aria-label",
      "class", // needed for Font Awesome
    ],

    /* ---------- Prevent javascript:, data:, vbscript: ---------- */
    ALLOWED_URI_REGEXP: /^(https?|mailto|tel):/i,

    /* ---------- Defense-in-depth ---------- */
    FORBID_TAGS: ["style", "script", "iframe"],
    FORBID_ATTR: ["onerror", "onclick"],
  });
}

export const containsHTML = (str: string) => /<[a-z][\s\S]*>/i.test(str);
